import { useState, useEffect, useRef } from 'react';
import { fetchWithAuth } from '../config/api';
import '../css/Phase2FormulaSelection.css';
import { normalizeProgressMessage } from '../utils/progressMessage';
import { MOCK_MODE } from '../config/mock';
import { loadCpcDescriptionMap, resolveCpcDescription } from '../utils/cpcCatalog';
import EditableLabel from './EditableLabel';
import IpcTooltipText from './IpcTooltip';
import { useLabels } from '../config/labelsContext';

interface SearchFormula {
  id: string;
  formula: string;
  formula_type:
    | 'MAIN_AND_RELATED_IPC'
    | 'MAIN_AND_KEYWORD'
    | 'MAIN_AND_RELATED_IPC_AND_DOMAIN'
    | 'MAIN_AND_KEYWORD_AND_DOMAIN';
  estimated_count: number;
  is_approximate?: boolean;
  primary_ipc?: string | null;
  primary_ipc_description?: string | null;
  related_ipcs?: string[];
  keywords?: string[];
  has_domain?: boolean;
}

interface Phase2FormulaSelectionProps {
  sessionId: string;
  onComplete: (data: unknown) => void;
  setIsLoading: (loading: boolean) => void;
  setLoadingMessage: (message: string) => void;
  showAlert?: (message: string, title?: string) => void;
}

const Phase2FormulaSelection = ({
  sessionId,
  onComplete,
  setIsLoading,
  setLoadingMessage,
  showAlert,
}: Phase2FormulaSelectionProps) => {
  const { labels } = useLabels();
  const [formulas, setFormulas] = useState<SearchFormula[]>([]);
  const [selectedIds, setSelectedIdsRaw] = useState<string[]>(() => {
    if (!sessionId) return [];
    try {
      const saved = localStorage.getItem(`formulaSelectedIds_${sessionId}`);
      return saved ? (JSON.parse(saved) as string[]) : [];
    } catch {
      return [];
    }
  });
  const setSelectedIds = (idsOrUpdater: string[] | ((prev: string[]) => string[])) => {
    setSelectedIdsRaw((prev) => {
      const next = typeof idsOrUpdater === 'function' ? idsOrUpdater(prev) : idsOrUpdater;
      if (sessionId) {
        try {
          localStorage.setItem(`formulaSelectedIds_${sessionId}`, JSON.stringify(next));
        } catch {
          /* ignore quota errors */
        }
      }
      return next;
    });
  };
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [cpcDescriptionMap, setCpcDescriptionMap] = useState<Record<string, string>>({});
  const executingRef = useRef(false);
  const restoreSavedSelection = (formulaIds: string[]) => {
    try {
      const saved = localStorage.getItem(`formulaSelectedIds_${sessionId}`);
      if (saved) {
        const ids = JSON.parse(saved) as string[];
        // Only restore IDs that still exist in the current formula list.
        const valid = ids.filter((id) => formulaIds.includes(id));
        if (valid.length > 0) {
          setSelectedIds(valid);
          return;
        }
      }
    } catch {
      /* ignore */
    }
    setSelectedIds([]);
  };
  const generatedForSessionRef = useRef<string | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resumeJobRanForSessionRef = useRef<string | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    executingRef.current = executing;
  }, [executing]);

  useEffect(() => {
    let cancelled = false;

    void loadCpcDescriptionMap().then((map) => {
      if (!cancelled) setCpcDescriptionMap(map);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Safety net: when this session's formula selection UI is ready, release only this session's
  // loading ownership. The scoped setters from App ignore stale/non-owner sessions, which keeps
  // concurrent sessions from closing each other's global loading modal.
  useEffect(() => {
    if (!loading && !executing) {
      setIsLoading(false);
      setLoadingMessage('');
    }
  }, [executing, loading, setIsLoading, setLoadingMessage]);

  useEffect(() => {
    if (!sessionId) return;
    if (generatedForSessionRef.current === sessionId) return;

    if (MOCK_MODE) {
      generatedForSessionRef.current = sessionId;
      const types: SearchFormula['formula_type'][] = [
        'MAIN_AND_KEYWORD',
        'MAIN_AND_RELATED_IPC',
        'MAIN_AND_RELATED_IPC_AND_DOMAIN',
        'MAIN_AND_KEYWORD_AND_DOMAIN',
      ];
      const ipcs = ['G06F', 'G01S', 'H04W', 'B64C', 'G08G', 'H04L', 'G05D', 'G01C'];
      const ipcDescriptions: Record<string, string> = {
        G06F: '電気的デジタルデータ処理',
        G01S: '無線による位置測定',
        H04W: '無線通信ネットワーク',
        B64C: '飛行機・ヘリコプター',
        G08G: '交通制御システム',
        H04L: 'デジタル情報の伝送',
        G05D: '非電気的変量の制御',
        G01C: '距離・水準・方位の測定',
      };
      const keywords = [
        'drone',
        'UAV',
        'position',
        'landing',
        'navigation',
        'sensor',
        'control',
        'signal',
        'tracking',
        'stabilization',
        'autopilot',
        'obstacle',
      ];

      const mockFormulas: SearchFormula[] = Array.from({ length: 40 }, (_, idx) => {
        const i = idx + 1;
        const ipcA = ipcs[idx % ipcs.length];
        const ipcB = ipcs[(idx * 3 + 2) % ipcs.length];
        const kwA = keywords[idx % keywords.length];
        const kwB = keywords[(idx * 5 + 1) % keywords.length];
        const t = types[idx % types.length];

        // Make estimated_count varied and roughly descending (to mimic "priority order").
        const estimated = Math.max(120, 1800 - i * 35 + ((idx % 7) * 30 - (idx % 3) * 20));

        const formula =
          t === 'MAIN_AND_RELATED_IPC'
            ? `(IPC=${ipcA} AND IPC=${ipcB})`
            : t === 'MAIN_AND_KEYWORD'
              ? `(IPC=${ipcA} AND (keyword=${kwA} OR keyword=${kwB}))`
              : t === 'MAIN_AND_RELATED_IPC_AND_DOMAIN'
                ? `(IPC=${ipcA} AND IPC=${ipcB} AND domain=mobility)`
                : `(IPC=${ipcA} AND (keyword=${kwA} OR keyword=${kwB}) AND domain=mobility)`;

        return {
          id: `mock-${i}`,
          formula,
          formula_type: t,
          estimated_count: estimated,
          primary_ipc: ipcA,
          primary_ipc_description: ipcDescriptions[ipcA] || null,
          related_ipcs:
            t === 'MAIN_AND_RELATED_IPC' || t === 'MAIN_AND_RELATED_IPC_AND_DOMAIN' ? [ipcB] : [],
          keywords:
            t === 'MAIN_AND_KEYWORD' || t === 'MAIN_AND_KEYWORD_AND_DOMAIN' ? [kwA, kwB] : [],
          has_domain:
            t === 'MAIN_AND_RELATED_IPC_AND_DOMAIN' || t === 'MAIN_AND_KEYWORD_AND_DOMAIN',
        };
      });
      setFormulas(mockFormulas);
      restoreSavedSelection(mockFormulas.map((f) => f.id));
      setLoading(false);
      setIsLoading(false);
      setLoadingMessage('');
      return;
    }

    cancelledRef.current = false;
    generatedForSessionRef.current = sessionId;
    resumeOrStartFormulaGeneration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Resume long-running final search job (if any) after refresh.
  useEffect(() => {
    if (!sessionId) return;
    if (resumeJobRanForSessionRef.current === sessionId) return;
    resumeJobRanForSessionRef.current = sessionId;

    const storedJobId = localStorage.getItem(`finalSearchJob_${sessionId}`) || '';
    if (storedJobId) {
      resumeFinalSearchJob(storedJobId);
      return;
    }

    // Fallback: ask backend for current status (in case localStorage was cleared).
    // Mark `executing=true` while we check, so the safety-net effect (which
    // releases the global loading modal once both `loading` and `executing`
    // are false) doesn't briefly close the modal in the gap between
    // resumeOrStartFormulaGeneration finishing (which sets loading=false on
    // is_complete sessions) and resumeFinalSearchJob taking ownership.
    setExecuting(true);
    (async () => {
      try {
        const statusRes = await fetchWithAuth(
          `/api/phase2/execute-final-search/status?session_id=${encodeURIComponent(sessionId)}`
        );
        if (statusRes.status === 202) {
          const data = (await statusRes.json()) as Record<string, unknown>;
          const jobId = typeof data.job_id === 'string' ? data.job_id : '';
          if (jobId) {
            localStorage.setItem(`finalSearchJob_${sessionId}`, jobId);
            // resumeFinalSearchJob keeps executing=true and owns the modal.
            resumeFinalSearchJob(jobId);
            return;
          }
          // 202 without a job id is unexpected; release executing.
          setExecuting(false);
        } else if (statusRes.ok) {
          const data = (await statusRes.json()) as Record<string, unknown>;
          if (data.status === 'completed') {
            // Search already completed; advance to the next phase.
            onComplete({ session_id: sessionId, next_step: 'extract_realization_methods' });
            return;
          }
          // Not running and not completed → no executing state to hold.
          setExecuting(false);
        } else {
          setExecuting(false);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(labels.phase2formula.searchStatusRestoreError, e);
        setExecuting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  const formatProgressMessage = (
    statusData: Record<string, unknown> | null | undefined,
    fallback: string
  ) => {
    const msgRaw = statusData?.progress_message;
    const base = typeof msgRaw === 'string' && msgRaw ? msgRaw : fallback;
    const msg = normalizeProgressMessage(base);

    const curRaw = statusData?.progress_current;
    const cur = typeof curRaw === 'number' ? curRaw : Number(curRaw ?? 0);

    const totalRaw = statusData?.progress_total;
    const total = totalRaw != null ? Number(totalRaw) : null;

    // Avoid showing "完了" while still in progress.
    if (total && cur < total && /完了/.test(msg)) {
      return `${labels.phase2formula.loadingMessage} (${cur.toLocaleString()}/${total.toLocaleString()})`;
    }
    return total ? `${msg} (${cur.toLocaleString()}/${total.toLocaleString()})` : msg;
  };

  const alertUI = (message: string) => {
    if (showAlert) return showAlert(message, labels.modals.noticeTitle);
    window.alert(message);
  };

  const startPolling = () => {
    if (!sessionId) return;
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    pollIntervalRef.current = setInterval(async () => {
      try {
        // If final search is executing, do not let formula polling overwrite the global loading message.
        if (executingRef.current) return;
        const statusRes = await fetchWithAuth(
          `/api/phase2/check-formulas-status?session_id=${encodeURIComponent(sessionId)}`
        );
        if (!statusRes.ok) return;

        const statusData = (await statusRes.json()) as Record<string, unknown>;
        if (statusData.status === 'failed') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          const err = typeof statusData.error_message === 'string' ? statusData.error_message : '';
          alertUI(err || labels.phase2formula.generateError);
          setLoading(false);
          setIsLoading(false);
          setLoadingMessage('');
          return;
        }
        // Only update message when formula generation is actually running or provides a real progress message.
        const st = typeof statusData.status === 'string' ? statusData.status : '';
        const msgRaw = statusData.progress_message;
        const hasMsg = typeof msgRaw === 'string' && msgRaw.trim() !== '';
        if (st === 'processing' || hasMsg) {
          setLoadingMessage(formatProgressMessage(statusData, labels.phase2formula.loadingMessage));
        }

        if (statusData.is_complete === true) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }

          const fs: SearchFormula[] = (statusData.formulas as SearchFormula[] | undefined) || [];
          setFormulas(fs);
          if (fs.length === 0) {
            alertUI(labels.phase2formula.zeroFormulasAlert);
          }
          restoreSavedSelection(fs.map((f) => f.id));

          setLoading(false);
          setIsLoading(false);
          setLoadingMessage('');
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Polling error:', e);
      }
    }, 5000);
  };

  const resumeOrStartFormulaGeneration = async () => {
    if (!sessionId) return;

    // Don't show loading modal yet — check backend status first so that
    // already-complete sessions never flash "検索式生成中..." on entry.

    try {
      // Guard: formula generation depends on prior steps (PDF analysis + classification).
      // If we enter this screen too early (e.g. via optimistic resume), wait until step>=8.
      // This avoids "0 formulas" due to empty pools before analysis completes.
      {
        let delayMs = 3000;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (cancelledRef.current) return;
          const stRes = await fetchWithAuth(
            `/api/phase2/check-status?session_id=${encodeURIComponent(sessionId)}`
          );
          if (stRes.ok) {
            const st = (await stRes.json()) as Record<string, unknown>;
            const step = Number(st.current_step ?? 0);
            const msgRaw = typeof st.progress_message === 'string' ? st.progress_message : '';
            const msg = msgRaw ? normalizeProgressMessage(msgRaw) : '';
            // If the session has already advanced past formula selection (search complete),
            // skip directly to the next phase instead of re-triggering formula generation
            // which would cause the backend to reset the step.
            if (step >= 15) {
              onComplete({ session_id: sessionId, next_step: 'extract_realization_methods' });
              return;
            }
            if (step >= 8) break;
            // Keep user informed while we wait for PDF analysis/classification.
            setLoading(true);
            if (!executingRef.current) {
              setIsLoading(true);
              setLoadingMessage(msg || labels.phase2formula.pdfAnalysisWaiting);
            }
          }
          await new Promise((r) => setTimeout(r, delayMs));
          delayMs = Math.min(Math.floor(delayMs * 1.2), 15000);
        }
      }

      // 1) First, check if generation is already running or finished (resume after refresh).
      const statusRes = await fetchWithAuth(
        `/api/phase2/check-formulas-status?session_id=${encodeURIComponent(sessionId)}`
      );
      if (statusRes.ok) {
        const statusData = (await statusRes.json()) as Record<string, unknown>;
        // If the session has already advanced past formula selection, skip to next phase.
        const statusStep = Number(statusData.current_step ?? 0);
        if (statusStep >= 15) {
          onComplete({ session_id: sessionId, next_step: 'extract_realization_methods' });
          return;
        }
        if (statusData.status === 'failed') {
          // backend already reset the session; fall through to POST /generate-formulas
        } else {
          // If already complete, hydrate UI immediately.
          if (statusData.is_complete === true) {
            const fs: SearchFormula[] = (statusData.formulas as SearchFormula[] | undefined) || [];
            setFormulas(fs);
            if (fs.length === 0) {
              alertUI(labels.phase2formula.zeroFormulasAlert);
            }
            restoreSavedSelection(fs.map((f) => f.id));
            setLoading(false);
            // Don't release the global loading modal here. The safety-net effect
            // (lines 111-116) handles it once both `loading` and `executing` are
            // false. If `resumeFinalSearchJob` has marked `executing=true` (a
            // final search is running for this session), this preserves the
            // modal — calling setIsLoading(false) explicitly here would race
            // against the parallel useEffect and close the modal even though
            // the user is in a non-interactive (executing) state.
            return;
          }

          // If already started (progress fields exist), just resume polling (do not re-trigger POST).
          if (statusData.progress_total != null || statusData.progress_message != null) {
            setLoading(true);
            if (!executingRef.current) {
              setIsLoading(true);
              setLoadingMessage(
                formatProgressMessage(statusData, labels.phase2formula.loadingMessage)
              );
            }
            startPolling();
            return;
          }
        }
      }

      // 2) Otherwise, start the generation once, then poll.
      if (!executingRef.current) {
        setIsLoading(true);
        setLoadingMessage(labels.phase2formula.loadingMessage);
      }
      // Set timeout for formula generation (60 minutes)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3600000); // 60 minutes

      const response = await fetchWithAuth(
        `/api/phase2/generate-formulas?session_id=${encodeURIComponent(sessionId)}`,
        {
          method: 'POST',
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(labels.phase2formula.generateError);

      await response.json();
      startPolling();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('検索式生成に失敗しました:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        alertUI(labels.phase2formula.generateTimeout);
      } else {
        alertUI(labels.phase2formula.generateError);
      }
      setLoading(false);
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const resumeFinalSearchJob = async (jobId: string) => {
    if (!sessionId || !jobId) return;

    // Stop formula polling so it won't overwrite search progress messages.
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setExecuting(true);
    setIsLoading(true);
    setLoadingMessage(labels.phase2formula.searchExecuting);

    try {
      let delayMs = 3000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const jobRes = await fetchWithAuth(`/api/jobs/${encodeURIComponent(jobId)}`);
        if (!jobRes.ok) throw new Error(labels.phase2formula.searchError);
        const job = await jobRes.json();

        const cur = Number(job.progress_current ?? 0);
        const total = job.progress_total != null ? Number(job.progress_total) : null;
        const msg = normalizeProgressMessage(
          (job.progress_message as string | undefined) || labels.common.processing
        );
        setLoadingMessage(
          total ? `${msg} (${cur.toLocaleString()}/${total.toLocaleString()})` : msg
        );

        if (job.status === 'succeeded') {
          localStorage.removeItem(`finalSearchJob_${sessionId}`);
          localStorage.removeItem(`formulaSelectedIds_${sessionId}`);
          onComplete(
            job.result ?? { session_id: sessionId, next_step: 'extract_realization_methods' }
          );
          break;
        }
        if (job.status === 'failed') {
          localStorage.removeItem(`finalSearchJob_${sessionId}`);
          throw new Error(job.error_message || labels.phase2formula.searchError);
        }

        await new Promise((r) => setTimeout(r, delayMs));
        delayMs = Math.min(Math.floor(delayMs * 1.2), 15000);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Resume job failed:', e);
      // If resume fails, clear job id to avoid infinite resume loops.
      localStorage.removeItem(`finalSearchJob_${sessionId}`);
    } finally {
      setExecuting(false);
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const handleExecuteSearch = async (selectedIdsOverride?: string[]) => {
    const ids = selectedIdsOverride ?? selectedIds;

    if (ids.length === 0) {
      if (showAlert) {
        showAlert(labels.phase2formula.selectAtLeastOne);
      } else {
        window.alert(labels.phase2formula.selectAtLeastOne);
      }
      return;
    }

    if (MOCK_MODE) {
      onComplete({ session_id: sessionId, selected_formula_ids: ids });
      return;
    }

    // Stop formula polling so it won't overwrite search progress messages.
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    // Show loading modal first, then set executing state
    setIsLoading(true);
    setLoadingMessage(labels.phase2formula.searchExecuting);
    setExecuting(true);

    try {
      // Set timeout for search execution (very long; job continues server-side even if tab reloads)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 12 * 60 * 60 * 1000); // 12 hours

      // 選択式のIDをセッションに保存
      await fetchWithAuth(`/api/phase2/select-formulas?session_id=${sessionId}`, {
        method: 'POST',
        body: JSON.stringify(ids),
        signal: controller.signal,
      });

      const response = await fetchWithAuth(
        `/api/phase2/execute-final-search?session_id=${sessionId}`,
        {
          method: 'POST',
          signal: controller.signal,
        }
      );

      if (response.status === 202) {
        const startData = await response.json();
        const jobId = startData.job_id as string | undefined;
        if (!jobId) throw new Error(labels.phase2formula.searchJobStartError);
        localStorage.setItem(`finalSearchJob_${sessionId}`, jobId);

        // Poll job status for progress + completion
        let delayMs = 3000;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const jobRes = await fetchWithAuth(`/api/jobs/${encodeURIComponent(jobId)}`, {
            signal: controller.signal,
          });
          if (!jobRes.ok) throw new Error(labels.phase2formula.searchError);
          const job = await jobRes.json();

          const cur = Number(job.progress_current ?? 0);
          const total = job.progress_total != null ? Number(job.progress_total) : null;
          const msg = normalizeProgressMessage(
            (job.progress_message as string | undefined) || labels.common.processing
          );
          setLoadingMessage(
            total ? `${msg} (${cur.toLocaleString()}/${total.toLocaleString()})` : msg
          );

          if (job.status === 'succeeded') {
            clearTimeout(timeoutId);
            localStorage.removeItem(`finalSearchJob_${sessionId}`);
            localStorage.removeItem(`formulaSelectedIds_${sessionId}`);
            onComplete(
              job.result ?? { session_id: sessionId, next_step: 'extract_realization_methods' }
            );
            break;
          }
          if (job.status === 'failed') {
            clearTimeout(timeoutId);
            localStorage.removeItem(`finalSearchJob_${sessionId}`);
            throw new Error(job.error_message || labels.phase2formula.searchError);
          }

          await new Promise((r) => setTimeout(r, delayMs));
          delayMs = Math.min(Math.floor(delayMs * 1.2), 15000);
        }
      } else {
        clearTimeout(timeoutId);
        if (!response.ok) throw new Error(labels.phase2formula.searchError);
        const data = await response.json();
        onComplete(data);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Search execution failed:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        alertUI(labels.phase2formula.searchTimeout);
      } else {
        alertUI(labels.phase2formula.searchError);
      }
    } finally {
      setExecuting(false);
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  if (loading) {
    return (
      <div className="phase2-formula-selection">
        <div className="phase-card">
          <div className="loading-state">
            <span className="spinner" />
            <div className="loading-state-text">
              <EditableLabel
                labelKey="phase2formula.loadingMessage"
                defaultValue="特許を集めるための検索条件を作成しています"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const buildFormulaLabel = (f: SearchFormula): string => {
    const cpcDesc = resolveCpcDescription(f.primary_ipc, cpcDescriptionMap);
    const ipcDesc = cpcDesc || f.primary_ipc_description || f.primary_ipc || '';
    const ftl = labels.phase2formula.formulaTypeLabels;

    switch (f.formula_type) {
      case 'MAIN_AND_RELATED_IPC':
        return ipcDesc ? `${ipcDesc}${ftl.mainAndRelatedIpc}` : ftl.mainAndRelatedIpcFallback;
      case 'MAIN_AND_KEYWORD':
        return ipcDesc ? `${ipcDesc}${ftl.mainAndKeyword}` : ftl.mainAndKeywordFallback;
      case 'MAIN_AND_RELATED_IPC_AND_DOMAIN':
        return ipcDesc
          ? `${ipcDesc}${ftl.mainAndRelatedIpcAndDomain}`
          : ftl.mainAndRelatedIpcAndDomainFallback;
      case 'MAIN_AND_KEYWORD_AND_DOMAIN':
        return ipcDesc
          ? `${ipcDesc}${ftl.mainAndKeywordAndDomain}`
          : ftl.mainAndKeywordAndDomainFallback;
      default:
        return f.formula_type;
    }
  };

  return (
    <div className="phase2-formula-selection">
      <div className="phase-card">
        <h2 className="phase-title">
          <span className="phase-title-step">
            <EditableLabel labelKey="phase2formula.stepLabel" defaultValue="Step2:" />
          </span>
          <span className="phase-title-text">
            <EditableLabel labelKey="phase2formula.title" defaultValue="特許情報の収集" />
          </span>
        </h2>
        <div className="workflow-step-description phase-description-banner">
          <EditableLabel
            labelKey="phase2formula.description"
            defaultValue="特許マップに掲載する対象の特許を収集（取得）するため、適した検索式（収集式）を選択してください"
          />
        </div>

        <div className="formula-selection-actions">
          <div className="selection-actions">
            <button
              type="button"
              onClick={() => setSelectedIds(formulas.map((f) => f.id))}
              className="select-all-btn"
            >
              {`検索式案 ${formulas.length}件全て選択`}
            </button>
            <button type="button" onClick={() => setSelectedIds([])} className="clear-all-btn">
              <EditableLabel labelKey="phase2formula.clearAllButton" defaultValue="選択解除" />
            </button>
          </div>
        </div>

        <div className="formulas-list">
          {formulas.length === 0 && (
            <div className="empty-state" style={{ padding: 16 }}>
              <p>
                <EditableLabel
                  labelKey="phase2formula.emptyFormulas"
                  defaultValue="検索式が0件のため表示できません。"
                />
              </p>
            </div>
          )}
          {formulas.map((formula, index) => (
            <div
              key={formula.id}
              className={`formula-item ${selectedIds.includes(formula.id) ? 'selected' : ''}`}
              onClick={() => toggleSelection(formula.id)}
            >
              <div className="formula-header">
                <div className="formula-heading">
                  {/* チェックボックス単体クリック時はカードの親クリックを止める */}
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(formula.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelection(formula.id)}
                    className="formula-checkbox"
                  />
                  <div className="formula-heading-text">
                    <div className="formula-name">
                      <EditableLabel
                        labelKey="phase2formula.formulaLabel"
                        defaultValue="検索式 案"
                      />
                      {index + 1}
                    </div>
                    <div className="formula-text">
                      <IpcTooltipText text={formula.formula} />
                    </div>
                  </div>
                </div>
              </div>

              {/* 下のメタ情報 */}
              <div className="formula-footer">
                <span className="formula-meta-chip formula-meta-primary">
                  {buildFormulaLabel(formula)}
                </span>

                <span className="estimated-count">
                  <EditableLabel
                    labelKey="phase2formula.hitEstimate"
                    defaultValue="ヒット見込み:"
                  />{' '}
                  {formula.estimated_count >= 0 ? (
                    `${formula.is_approximate ? '約' : ''}${formula.estimated_count.toLocaleString()}件`
                  ) : (
                    <EditableLabel labelKey="phase2formula.hitUnknown" defaultValue="不明" />
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="formula-actions-bottom">
          <button
            type="button"
            onClick={() => handleExecuteSearch()}
            className="btn-primary formula-submit-btn"
            disabled={executing}
          >
            {executing ? (
              <>
                <span className="spinner" />
                <EditableLabel
                  labelKey="phase2formula.executingButton"
                  defaultValue="特許を収集しています"
                />
              </>
            ) : (
              <>{`選択した ${selectedIds.length}件の検索式で実行`}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Phase2FormulaSelection;
